import {
  type Asset,
  ENHANCE_MODELS,
  type EnhanceRequestInput,
  enhanceModelsFor,
  enhanceProblem,
  enhanceScaleChoices,
  enhanceSourceSize,
  estimateJobSec,
  MODEL_LOAD_SEC,
} from "@studio/shared";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { enhanceDraftModel, enhanceScaleFor, useComposer } from "@/lib/composer";
import { isActiveJob, useLive } from "@/lib/events";
import { cn, formatDuration, formatWait } from "@/lib/format";
import { KIND_OPTIONS, SlateSelect, SlotTile } from "./PromptDock";
import { SlotPicker } from "./SlotPicker";
import { Button, Segmented } from "./ui";

/**
 * The dock of the Enhance tab: one picture or video in, the same one made better out (restored and enlarged,
 * cleared of noise, or turned into HDR, by the model that is picked). There is no prompt to write, so the place
 * of the prompt says what will happen to the file that was picked.
 */
export function EnhanceDock() {
  const navigate = useNavigate();
  const composer = useComposer();
  const { enhance } = composer;
  const { jobs, gpu } = useLive();
  const capabilities = useQuery({ queryKey: ["capabilities"], queryFn: api.capabilities, staleTime: 60_000 });
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(false);

  const source = enhance.source;
  const model = source ? enhanceDraftModel(source.kind, enhance.model) : null;
  const info = model ? ENHANCE_MODELS[model] : null;
  // what the model works from: a video's original (what was uploaded or made), not the copy the studio plays
  const size = source ? enhanceSourceSize(source) : { width: null, height: null };
  // The scale that was chosen, as this model can run it for this file: a model with a single size runs at that
  // one, and the choice stays for the models that have one.
  const scale = (source && model ? enhanceScaleFor(source, enhance.scale, model) : null) ?? enhance.scale;
  const problemAt = (at: number) =>
    source && model ? enhanceProblem({ model, scale: at, kind: source.kind, ...size }) : null;
  // A studio that says which models its GPU servers have is believed; an older one is asked and answers itself.
  const offered = capabilities.data?.enhance;
  // a model is taken as available unless the studio says no GPU server has it
  const has = (m: string) => !offered || offered.byModel.some((b) => b.model === m && b.available);
  const unavailable = model && info && !has(model) ? `No GPU server can run ${info.label} right now.` : null;
  const problem = problemAt(scale) ?? unavailable;
  const choices =
    model && size.width && size.height ? enhanceScaleChoices(model, size.width, size.height) : [];
  const out = choices.find((c) => c.scale === scale) ?? null;
  const modelOptions = source
    ? enhanceModelsFor(source.kind).map((m) => ({
        value: m,
        short: ENHANCE_MODELS[m].label,
        label: `${ENHANCE_MODELS[m].label} · ${ENHANCE_MODELS[m].does}${has(m) ? "" : " (no GPU server has it now)"}`,
        title: ENHANCE_MODELS[m].note,
      }))
    : [];

  const request: EnhanceRequestInput | null =
    source && model && !problem
      ? {
          type: "enhance",
          model,
          sourceAssetId: source.id,
          scale,
          seed: composer.reuseSeed && enhance.seed !== null ? enhance.seed : undefined,
        }
      : null;

  const estimate = (() => {
    if (!request || !source || !model) return null;
    const ahead = Object.values(jobs)
      .filter(isActiveJob)
      .reduce((sum, job) => sum + (job.etaSec ?? 0), 0);
    const load = gpu?.activeModel === model ? 0 : (MODEL_LOAD_SEC[model] ?? 30);
    const work = estimateJobSec({
      type: "enhance",
      model,
      sourceAssetId: source.id,
      scale,
      sourceWidth: size.width ?? undefined,
      sourceHeight: size.height ?? undefined,
      sourceDurationSec: source.durationSec ?? undefined,
    });
    return ahead + load + work;
  })();

  // Each scale says the size it gives: the model has a smallest and a largest frame of its own, so 1× of an
  // upright 1080×1920 video is 1280×2276, and everything from 1.5× up of a 1512×2688 one is 2160×3840.
  const scaleOptions = choices.map((c) => {
    const blocked =
      problemAt(c.scale) ??
      (c.same ? `The largest size ${info?.label} writes for this file is reached at a smaller scale.` : null);
    const result = `${c.width}×${c.height}`;
    return {
      value: c.scale,
      short: `${c.scale}×`,
      label: `${c.scale}× → ${result}`,
      disabled: blocked !== null,
      title: blocked ?? `Restore to ${result}`,
    };
  });

  const setSource = (asset: Asset) => {
    const note = composer.setEnhanceSource(asset);
    if (note) toast.error(note);
  };

  const addFiles = async (files: File[]) => {
    const file = files.find((f) => /^(image|video)\//.test(f.type));
    if (!file) {
      toast.error("Paste or drop a picture or a video.");
      return;
    }
    const pending = toast.loading("Adding the file…");
    try {
      setSource(await api.upload(file));
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      toast.dismiss(pending);
    }
  };

  const dropzone = useDropzone({
    noClick: true,
    noKeyboard: true,
    noPaste: true,
    onDrop: (files) => void addFiles(files),
  });

  const submit = async () => {
    if (!request || busy) return;
    setBusy(true);
    try {
      const job = await api.createJob({ request, parentJobId: composer.parentJobId ?? undefined });
      useLive.getState().upsertJob(job);
      composer.setParent(null);
      composer.setEnhance({ seed: null });
      composer.setReuseSeed(true);
      document.getElementById("run-feed")?.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      {...dropzone.getRootProps()}
      className="relative mx-auto w-full max-w-[880px] overflow-x-clip px-3 pb-3 md:px-6 md:pb-4"
    >
      <input {...dropzone.getInputProps()} />
      <div
        onPaste={(e) => {
          const files = Array.from(e.clipboardData.files);
          if (files.length === 0) return;
          e.preventDefault();
          void addFiles(files);
        }}
        className={cn(
          "rounded-[var(--radius-dock)] border border-line bg-panel shadow-[0_14px_40px_-22px_rgba(0,0,0,0.7)] transition-colors",
          dropzone.isDragActive && "border-film",
        )}
      >
        {composer.parentJobId ? (
          <div className="flex items-center gap-2 border-b border-line px-3 py-1.5 text-[13px] text-mute">
            <span className="min-w-0 flex-1 truncate">Editing an enhancement you made.</span>
            <button
              type="button"
              onClick={() => {
                composer.clear("enhance");
                composer.setParent(null);
                composer.setReuseSeed(true);
                toast("Started fresh");
              }}
              title="Start fresh"
              className="-mr-1 inline-flex size-6 shrink-0 items-center justify-center rounded-[8px] text-mute hover:bg-raised hover:text-paper"
            >
              <span className="sr-only">Stop editing and start fresh</span>
              <X className="size-4" />
            </button>
          </div>
        ) : null}

        <div className="flex items-center gap-3 px-3 pt-2.5">
          <div className="shrink-0 py-0.5">
            <SlotTile
              label="Picture or video"
              name="file to enhance"
              asset={source}
              onAdd={() => setPicker(true)}
              onRemove={() => composer.setEnhance({ source: null, seed: null })}
            />
          </div>
          <div className="min-w-0 flex-1 py-1 text-[15px] leading-6">
            {source && info ? (
              <>
                <div className="flex min-w-0 items-center text-paper">
                  {/* the name of the model is where another one for this kind of file is picked */}
                  {modelOptions.length > 1 && model ? (
                    <SlateSelect
                      label="What to do with it"
                      className="-ml-2 text-[15px]"
                      value={model}
                      onChange={(picked) => composer.setEnhance({ model: picked })}
                      options={modelOptions}
                    />
                  ) : (
                    <span className="shrink-0">{info.label}</span>
                  )}
                  <span className="tabular min-w-0 truncate whitespace-pre">
                    {out
                      ? info.hdr
                        ? ` · ${out.width}×${out.height} → HDR`
                        : ` · ${size.width}×${size.height} → ${out.width}×${out.height}`
                      : ""}
                    {source.kind === "video" && source.durationSec
                      ? ` · ${formatDuration(source.durationSec)}`
                      : ""}
                  </span>
                </div>
                <p
                  className={cn("line-clamp-2 text-[13px] leading-5", problem ? "text-danger" : "text-mute")}
                >
                  {problem ?? info.note}
                </p>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setPicker(true)}
                className="block w-full text-left text-[16px] text-mute/80 outline-none hover:text-mute"
              >
                Add a picture or a video to restore, enlarge, clear of noise or turn into HDR — drop it here,
                paste it, or pick one
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 px-2 pb-2 pt-2 sm:gap-2">
          <Segmented
            size="sm"
            className="shrink-0"
            value={"enhance" as const}
            onChange={(value) => navigate({ to: "/create", search: { kind: value } })}
            options={KIND_OPTIONS}
          />
          {info && scaleOptions.length > 1 ? (
            <>
              {/* a row of buttons where there is room for it; on a phone the scale is one value that opens a list */}
              <Segmented
                size="sm"
                className="hidden shrink-0 sm:inline-flex"
                value={scale}
                onChange={(picked) => composer.setEnhance({ scale: picked })}
                options={scaleOptions.map((o) => ({ ...o, label: o.short }))}
              />
              <SlateSelect
                label="How much larger"
                className="sm:hidden"
                value={scale}
                onChange={(picked) => composer.setEnhance({ scale: picked })}
                options={scaleOptions}
              />
            </>
          ) : null}
          <p className="tabular ml-auto hidden min-w-0 truncate text-right text-[13px] text-mute sm:block">
            {estimate ? `about ${formatWait(estimate)}` : null}
          </p>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={!request || busy}
            className="ml-auto min-w-[64px] shrink-0 px-3 sm:ml-0 sm:min-w-[96px]"
          >
            {busy ? "Starting…" : "Enhance"}
          </Button>
        </div>
      </div>

      {picker ? (
        <SlotPicker
          open
          onOpenChange={setPicker}
          title="Picture or video to enhance"
          description="A picture is restored or cleared of noise, and enlarged up to four times; a video is restored up to 4K or turned into HDR, with its sound kept."
          kinds={["image", "video"]}
          onPick={setSource}
        />
      ) : null}
    </div>
  );
}
